class VideoControl extends HTMLElement {
  constructor() {
    super();
    this.eventAdded = false;
    this.timer = null;
    this.isDragging = false;

    this.showControl = this.showControl.bind(this);
    this.startTimeLineDrag = this.startTimeLineDrag.bind(this);
    this.desktop = window.matchMedia("(min-width: 990px)").matches;
  }

  connectedCallback() {
    this.addEventListener("mousemove", this.showControl);
    this.addEventListener("click", this.showControl);
    this.setEventListener();
  }

  togglePlayPause(e) {
    if (!this.video || e.target.closest(".bottom-control")) return;
    this.classList.toggle("playing", this.video.paused);
    this.classList.toggle("paused", !this.video.paused);
    this.playPauseButton.setAttribute(
      "aria-label",
      this.video.paused ? "pause" : "play"
    );
    this.video.paused ? this.video.play() : this.video.pause();
  }

  showControl(e) {
    e?.stopPropagation();
    e?.preventDefault();
    if (!this.eventAdded) this.setEventListener();
    this.classList.add("hovering");
    const visibleTime = this.desktop ? 100 : 1500;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.classList.remove("hovering");
    }, visibleTime);
  }

  playVideo() {
    this.classList.remove("paused");
    if (!this.desktop) {
      this.showControl();
    }
  }

  setEventListener() {
    this.video = this.parentNode.querySelector("video");
    if (!this.video || this.eventAdded) return;
    this.timeline = this.querySelector(".custom-timeline");
    this.fill = this.querySelector(".timeline-fill");
    this.dot = this.querySelector(".timeline-dot");
    this.timestamp = this.querySelector(".video-timestamp");
    this.loader = this.querySelector(".videoLoader");
    this.muteBtn = this.querySelector(".mute-button");
    this.fullscreenBtn = this.querySelector(".fullscreen-button");
    this.playPauseButton = this.querySelector(".play-pause-button");
    this.currentVolume = 0;

    this.addEventListener("click", (e) => this.togglePlayPause(e));
    this.playPauseButton.addEventListener("click", (e) => this.togglePlayPause(e));
    this.playPauseButton.addEventListener("keydown", (e) => {
      if (e.key.toUpperCase() === 'ENTER') {
        this.togglePlayPause(e)
      }
    });
    this.dot.addEventListener("mousedown", () => this.startTimeLineDrag());
    this.dot.addEventListener("touchstart", () => this.startTimeLineDrag());
    this.muteBtn.addEventListener("click", () => this.handleMute());
    this.video.addEventListener("timeupdate", () => this.timeLineUpdate());
    this.video.addEventListener("loadedmetadata", () => this.initTimeStamp());
    this.timeline.addEventListener("click", (e) => this.handleClickTimeLine(e));
    this.fullscreenBtn.addEventListener("click", () => this.toggleFullScreen());
    this.video.addEventListener("waiting", () => this.handleLoader(true));
    this.video.addEventListener("loadstart", () => this.handleLoader(true));
    this.video.addEventListener("seeking", () => this.handleLoader(true));
    this.video.addEventListener("seeked", () => this.handleLoader(false));
    this.video.addEventListener("canplay", () => this.handleLoader(false));
    this.video.addEventListener("playing", () => this.handleLoader(false));
    this.eventAdded = true;
  }

  handleMute() {
    this.video.muted = !this.video.muted;
    this.muteBtn.classList.toggle("muted", this.video.muted);
    this.muteBtn.setAttribute("aria-label", this.video.muted ? "unmute" : "mute");
  }

  toggleFullScreen() {
    const fullScreen = this.parentNode.classList.contains("fullscreen");
    const handleScreen = (open) => {
      document.body.classList.toggle("overflow-hidden", open);
      this.parentNode.classList.toggle("fullscreen", open);
      this.fullscreenBtn.setAttribute(
        "aria-label",
        open ? "exit full screen" : "enter full screen"
      );
      open ? setTimeout(() => trapFocus(this, this.playPauseButton), 100) : removeTrapFocus(this.fullscreenBtn);
      open ? this.setAttribute('aria-modal', 'true') : this.removeAttribute('aria-modal');
      open ? this.setAttribute('role', 'dialog') : this.removeAttribute('role');
    };
    handleScreen(!fullScreen);
    const closeByEsc = (e) => {
      if (e.key.toUpperCase() === "ESCAPE") {
        handleScreen(false);
        e.stopPropagation();
        this.removeEventListener("keydown", closeByEsc);
      }
    };
    if (!fullScreen) {
      this.addEventListener("keydown", closeByEsc);
    }
  }

  startTimeLineDrag(e) {
    this.isDragging = true;
    document.body.style.userSelect = "none";

    const move = (moveEvent) => {
      const clientX = moveEvent.clientX || moveEvent.touches?.[0]?.clientX;
      const rect = this.timeline.getBoundingClientRect();
      let percent = (clientX - rect.left) / rect.width;
      percent = Math.max(0, Math.min(1, percent));
      this.fill.style.width = `${percent * 100}%`;
      this.video.currentTime = percent * this.video.duration;
    };

    const stop = () => {
      this.isDragging = false;
      document.body.style.userSelect = "";
      this.removeEventListener("mousemove", move);
      this.removeEventListener("mouseup", stop);
      this.removeEventListener("touchmove", move);
      this.removeEventListener("touchend", stop);
    };

    this.addEventListener("mousemove", move);
    this.addEventListener("mouseup", stop);
    this.addEventListener("touchmove", move);
    this.addEventListener("touchend", stop);
  }

  initTimeStamp() {
    const current = this.formatTime(0);
    const total = this.formatTime(this.video.duration || 0);
    this.timestamp.textContent = `${current} / ${total}`;
  }

  timeLineUpdate() {
    if (!this.isDragging && this.video.duration) {
      const total = this.formatTime(this.video.duration || 0);
      const current = this.formatTime(this.video.currentTime);
      this.timestamp.textContent = `${current} / ${total}`;
      const percent = (this.video.currentTime / this.video.duration) * 100;
      this.fill.style.width = `${percent}%`;
    }
  }

  handleLoader(load) {
    this.loader.style.display = load ? "block" : "none";
    this.classList.toggle("loading", load);
  }

  handleClickTimeLine(e) {
    const rect = this.timeline.getBoundingClientRect();
    const x = e.clientX || e.touches?.[0]?.clientX;
    const percent = Math.min(1, Math.max(0, (x - rect.left) / rect.width));
    this.fill.style.width = `${percent * 100}%`;
    this.video.currentTime = percent * this.video.duration;
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }
}

customElements.define("video-control", VideoControl);